(() => {
  if (customElements.get('rdb-merch-badge')) return;

  const reducedMotion = matchMedia('(prefers-reduced-motion: reduce)');
  const badges = new Set();
  let paused = false;
  try { paused = sessionStorage.getItem('rdb-merch-paused') === 'true'; } catch {}

  const refreshControls = () => {
    document.querySelectorAll('[data-merch-motion-toggle]').forEach(button => {
      button.hidden = reducedMotion.matches || badges.size === 0;
      button.setAttribute('aria-pressed', String(paused));
      const label = paused ? button.dataset.labelResume : button.dataset.labelPause;
      button.setAttribute('aria-label', label);
      button.title = label;
    });
  };

  const refresh = () => {
    badges.forEach(badge => badge.updateMotion());
    refreshControls();
  };

  class MerchBadge extends HTMLElement {
    connectedCallback() {
      if (this.dataset.motion === 'static' || this.controller) return;
      this.controller = new AbortController();
      this.visible = false;
      this.hovered = false;
      this.focused = false;
      badges.add(this);
      const card = this.closest('.d2-product-card');
      const listen = (event, callback) => card?.addEventListener(event, callback, {signal: this.controller.signal});
      this.addEventListener('pointerenter', event => { this.hovered = event.pointerType !== 'touch'; this.updateMotion(); }, {signal: this.controller.signal});
      this.addEventListener('pointerleave', () => { this.hovered = false; this.updateMotion(); }, {signal: this.controller.signal});
      listen('focusin', () => { this.focused = true; this.updateMotion(); });
      listen('focusout', event => {
        if (!card.contains(event.relatedTarget)) { this.focused = false; this.updateMotion(); }
      });
      this.observer = new IntersectionObserver(entries => {
        this.visible = entries[0].isIntersecting && entries[0].intersectionRatio >= .9;
        this.updateMotion();
      }, {threshold: [0, .9, 1]});
      this.observer.observe(this);
      if (this.dataset.motion === 'line') {
        const sequence = this.querySelector('.d2-merch-strip__sequence');
        this.originalItems = [...sequence.children].map(item => item.cloneNode(true));
        this.resizeObserver = new ResizeObserver(() => this.measureTicker());
        this.resizeObserver.observe(this.querySelector('.d2-merch-strip__viewport'));
        document.fonts.ready.then(() => { if (this.isConnected) this.measureTicker(); });
      }
      refreshControls();
    }

    measureTicker() {
      const viewport = this.querySelector('.d2-merch-strip__viewport');
      if (!viewport?.clientWidth || !this.hasAttribute('data-ready') || !this.originalItems) return;
      const [first, second] = this.querySelectorAll('.d2-merch-strip__sequence');
      const items = () => this.originalItems.map(item => item.cloneNode(true));
      first.replaceChildren(...items());
      for (let repeats = 0; repeats < 8 && first.getBoundingClientRect().width < viewport.clientWidth; repeats += 1) first.append(...items());
      second.replaceChildren(...[...first.children].map(item => item.cloneNode(true)));
      this.style.setProperty('--merch-duration', (first.getBoundingClientRect().width / 22).toFixed(3) + 's');
    }

    updateMotion() {
      const ready = !reducedMotion.matches;
      const wasReady = this.hasAttribute('data-ready');
      this.toggleAttribute('data-ready', ready);
      if (ready && !wasReady) this.measureTicker();
      this.toggleAttribute('data-running', ready && !paused && this.visible && !this.hovered && !this.focused && !document.hidden && !this.closest('[hidden]'));
    }

    disconnectedCallback() {
      this.observer?.disconnect();
      this.resizeObserver?.disconnect();
      this.controller?.abort();
      this.controller = null;
      badges.delete(this);
      this.removeAttribute('data-running');
      refreshControls();
    }
  }

  customElements.define('rdb-merch-badge', MerchBadge);
  document.addEventListener('click', event => {
    if (!event.target.closest('[data-merch-motion-toggle]')) return;
    paused = !paused;
    try { sessionStorage.setItem('rdb-merch-paused', String(paused)); } catch {}
    refresh();
  });
  document.addEventListener('visibilitychange', refresh);
  document.addEventListener('rdb:products-filtered', refresh);
  document.addEventListener('shopify:section:load', refresh);
  reducedMotion.addEventListener('change', refresh);
  addEventListener('pagehide', () => badges.forEach(badge => badge.removeAttribute('data-running')));
  addEventListener('pageshow', refresh);
  refreshControls();
})();
